import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Lightweight image overlay component for zooming into preview images
 * Shows full-size image in a modal overlay with click-outside-to-close functionality
 */
const ImageOverlay = ({ imageUrl, alt = 'Preview', isOpen, onClose }) => {
  const overlayRef = useRef(null);
  const imageRef = useRef(null);

  // Handle escape key and focus management
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Prevent body scroll when overlay is open
    const originalBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown);

    // Focus the overlay for keyboard accessibility
    if (overlayRef.current) {
      overlayRef.current.focus();
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Handle mouse up outside image to close (using mouseup instead of click for better compatibility)
  const handleOverlayMouseUp = (event) => {
    // Close if clicking on the overlay background or container (but not the image or close button)
    if (event.target === overlayRef.current || event.target.classList.contains('image-overlay-container')) {
      onClose();
    }
  };

  // Handle close button click
  const handleCloseClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClose();
  };

  // Handle close button mouse down (primary close method for better reliability)
  const handleCloseMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClose();
  };

  // Handle image load to remove loading state if needed
  const handleImageLoad = () => {
    // Image loaded successfully - could add analytics or state updates here
  };

  const handleImageError = () => {
    // Handle case where full-size image fails to load
    console.warn('Failed to load full-size image:', imageUrl);
  };

  if (!isOpen || !imageUrl) {
    return null;
  }

  // Render the overlay using a portal to ensure it appears above all other content
  return createPortal(
    <div
      ref={overlayRef}
      className="image-overlay"
      onMouseUp={handleOverlayMouseUp}
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      tabIndex={-1}
    >
      <div className="image-overlay-container">
        <button
          className="image-overlay-close"
          onClick={handleCloseClick}
          onMouseDown={handleCloseMouseDown}
          aria-label="Close image preview"
          type="button"
        >
          ×
        </button>
        <img
          ref={imageRef}
          src={imageUrl}
          alt={alt}
          className="image-overlay-content"
          onLoad={handleImageLoad}
          onError={handleImageError}
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking image
        />
      </div>
    </div>,
    document.body
  );
};

export default ImageOverlay;